import React, { useState, useCallback, useMemo, useRef, useEffect } from 'react';
import {
  Input,
  Select,
  Button,
  Spinner,
} from '../../components';
import type { SelectOption } from '../../components';
import { Cross2Icon, TrashIcon } from '@radix-ui/react-icons';
import { ASSETS } from '../../constants/assets';
import {
  CreateEmployeeRequest,
  EMPLOYEE_ROLE_LABELS,
  EmployeeApiRole,
  UpdateEmployeeRequest,
} from '../../types/employee';
import { useEmployeeStore } from '../../store/employeeStore';
import { employeeService } from '../../services/employeeService';
import { branchService } from '../../services/branchService';
import { sharedService, type UploadedFile } from '../../services/sharedService';
import { useEmailValidation } from '../../hooks/useEmailValidation';
import { validateEmail } from '../../utils/validation';
import './EmployeeFormPanel.scss';

const FORM_ICONS = ASSETS.icons.createUser;

interface EmployeeFormData {
  fullName: string;
  firstName: string;
  lastName: string;
  email: string;
  role: EmployeeApiRole | '';
  branchId: string;
  sendActivationEmail: boolean;
}

const INITIAL_FORM: EmployeeFormData = {
  fullName: '',
  firstName: '',
  lastName: '',
  email: '',
  role: '',
  branchId: '',
  sendActivationEmail: true,
};

export type EmployeeFormPanelMode = 'create' | 'edit';

export interface EmployeeFormPanelProps {
  isOpen: boolean;
  mode: EmployeeFormPanelMode;
  employeeId?: number;
  initialProfilePictureUrl?: string | null;
  onClose: () => void;
  onSuccess?: () => void;
}

export const EmployeeFormPanel: React.FC<EmployeeFormPanelProps> = ({
  isOpen,
  mode,
  employeeId,
  initialProfilePictureUrl,
  onClose,
  onSuccess,
}) => {
  const isEditMode = mode === 'edit';
  const { createEmployee, fetchEmployees, isLoading, error, clearError } =
    useEmployeeStore();

  const [formData, setFormData] = useState<EmployeeFormData>(INITIAL_FORM);
  const [roleOptions, setRoleOptions] = useState<SelectOption[]>([]);
  const [branchOptions, setBranchOptions] = useState<SelectOption[]>([]);
  const [profilePhoto, setProfilePhoto] = useState<UploadedFile | null>(null);
  const [photoPreview, setPhotoPreview] = useState<string | null>(null);
  const [photoError, setPhotoError] = useState<string | null>(null);
  const [isUploadingPhoto, setIsUploadingPhoto] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const {
    error: emailError,
    onBlur: onEmailBlur,
    onChange: onEmailChange,
    validate: validateEmailField,
    reset: resetEmailValidation,
  } = useEmailValidation();
  const [isLoadingEmployee, setIsLoadingEmployee] = useState(false);
  const [isLoadingMeta, setIsLoadingMeta] = useState(false);
  const [loadError, setLoadError] = useState<string | null>(null);

  const photoInputRef = useRef<HTMLInputElement>(null);

  const loadBranches = useCallback(async () => {
    const branches = await branchService.getBranches();
    setBranchOptions(
      branches.map((branch) => ({
        value: String(branch.branchId),
        label: branch.name,
      }))
    );
    return branches;
  }, []);

  const resetForm = useCallback(() => {
    setFormData(INITIAL_FORM);
    setProfilePhoto(null);
    setPhotoPreview(null);
    setPhotoError(null);
    setLoadError(null);
    resetEmailValidation();
    clearError();
  }, [clearError, resetEmailValidation]);

  useEffect(() => {
    if (!isOpen) {
      resetForm();
      return;
    }

    let cancelled = false;

    (async () => {
      setIsLoadingMeta(true);
      try {
        const [roles] = await Promise.all([
          employeeService.getAssignableRoles(),
          loadBranches(),
        ]);

        if (cancelled) return;

        setRoleOptions(
          roles.map((role) => ({
            value: role.name,
            label: EMPLOYEE_ROLE_LABELS[role.name],
          }))
        );
      } catch {
        if (!cancelled) {
          setLoadError('Failed to load roles and branches.');
        }
      } finally {
        if (!cancelled) {
          setIsLoadingMeta(false);
        }
      }
    })();

    if (!isEditMode || !employeeId) {
      resetForm();
      return () => {
        cancelled = true;
      };
    }

    (async () => {
      setIsLoadingEmployee(true);
      setLoadError(null);
      try {
        const employee = await employeeService.getEmployeeById(employeeId);
        if (!cancelled) {
          setFormData({
            fullName: `${employee.firstName} ${employee.lastName}`.trim(),
            firstName: employee.firstName,
            lastName: employee.lastName,
            email: employee.email,
            role: employee.role,
            branchId: String(employee.branchId),
            sendActivationEmail: false,
          });
          setPhotoPreview(initialProfilePictureUrl ?? null);
          if (employee.profilePictureId) {
            setProfilePhoto({
              id: employee.profilePictureId,
              url: initialProfilePictureUrl ?? '',
            });
          }
        }
      } catch {
        if (!cancelled) {
          setLoadError('Failed to load employee details.');
        }
      } finally {
        if (!cancelled) {
          setIsLoadingEmployee(false);
        }
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [isOpen, isEditMode, employeeId, initialProfilePictureUrl, resetForm, loadBranches]);

  useEffect(() => {
    if (!isOpen) return;

    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', handleEscape);
    document.body.style.overflow = 'hidden';

    return () => {
      document.removeEventListener('keydown', handleEscape);
      document.body.style.overflow = '';
    };
  }, [isOpen, onClose]);

  const handleInputChange = useCallback(
    <K extends keyof EmployeeFormData>(field: K, value: EmployeeFormData[K]) => {
      clearError();
      setFormData((prev) => ({ ...prev, [field]: value }));
    },
    [clearError]
  );

  const handleFullNameChange = useCallback(
    (value: string) => {
      clearError();
      const parts = value.trim().split(/\s+/).filter(Boolean);
      setFormData((prev) => ({
        ...prev,
        fullName: value,
        firstName: parts[0] || '',
        lastName: parts.length > 1 ? parts.slice(1).join(' ') : '',
      }));
    },
    [clearError]
  );

  const validateImageFile = useCallback((file: File): string | null => {
    const validTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/webp'];
    if (!validTypes.includes(file.type)) {
      return 'Only JPEG, PNG, and WebP images are allowed';
    }
    if (file.size > 5 * 1024 * 1024) {
      return 'Image size must be less than 5MB';
    }
    return null;
  }, []);

  const handlePhotoSelect = useCallback(
    async (event: React.ChangeEvent<HTMLInputElement>) => {
      const file = event.target.files?.[0];
      if (!file) return;

      const validationError = validateImageFile(file);
      if (validationError) {
        setPhotoError(validationError);
        return;
      }

      setPhotoError(null);
      const previewUrl = URL.createObjectURL(file);
      setPhotoPreview(previewUrl);
      setIsUploadingPhoto(true);

      try {
        const uploadedFiles = await sharedService.uploadFiles([file]);
        if (uploadedFiles.length > 0) {
          const uploadedFile = uploadedFiles[0];
          setProfilePhoto(uploadedFile);
          setPhotoPreview(uploadedFile.url);
        } else {
          setPhotoError('Failed to upload photo. Please try again.');
          setPhotoPreview(profilePhoto?.url ?? initialProfilePictureUrl ?? null);
        }
      } catch {
        setPhotoError('Failed to upload photo. Please try again.');
        setPhotoPreview(profilePhoto?.url ?? initialProfilePictureUrl ?? null);
      } finally {
        setIsUploadingPhoto(false);
        if (photoInputRef.current) {
          photoInputRef.current.value = '';
        }
      }
    },
    [validateImageFile, profilePhoto, initialProfilePictureUrl]
  );

  const handleRemovePhoto = useCallback(() => {
    setProfilePhoto(null);
    setPhotoPreview(null);
    setPhotoError(null);
  }, []);

  const triggerPhotoInput = useCallback(() => {
    if (!isUploadingPhoto) {
      photoInputRef.current?.click();
    }
  }, [isUploadingPhoto]);

  const isFormValid = useMemo(
    () =>
      !!(
        formData.fullName.trim() &&
        validateEmail(formData.email).valid &&
        formData.role &&
        formData.branchId
      ),
    [formData]
  );

  const handleSubmit = useCallback(async () => {
    if (!isFormValid || isUploadingPhoto) return;

    const emailResult = validateEmailField(formData.email);
    if (!emailResult.valid) {
      return;
    }

    setIsSaving(true);
    try {
      const profilePictureId = profilePhoto?.id;

      if (isEditMode && employeeId) {
        const payload: UpdateEmployeeRequest = {
          firstName: formData.firstName.trim(),
          lastName: formData.lastName.trim(),
          email: emailResult.trimmed,
          role: formData.role as EmployeeApiRole,
          branchId: Number(formData.branchId),
        };

        if (profilePictureId) {
          payload.profilePictureId = profilePictureId;
        }

        await employeeService.updateEmployee(employeeId, payload);
        await fetchEmployees();
      } else {
        const payload: CreateEmployeeRequest = {
          firstName: formData.firstName.trim(),
          lastName: formData.lastName.trim() || undefined,
          email: emailResult.trimmed,
          role: formData.role as EmployeeApiRole,
          branchId: Number(formData.branchId),
          sendActivationEmail: formData.sendActivationEmail,
        };

        if (profilePictureId) {
          payload.profilePictureId = profilePictureId;
        }

        await createEmployee(payload);
      }

      onSuccess?.();
      onClose();
    } catch (err) {
      console.error('Failed to save employee:', err);
    } finally {
      setIsSaving(false);
    }
  }, [
    isFormValid,
    isUploadingPhoto,
    formData,
    profilePhoto,
    isEditMode,
    employeeId,
    createEmployee,
    fetchEmployees,
    onClose,
    onSuccess,
  ]);

  if (!isOpen) return null;

  const isPanelLoading = isLoadingEmployee || isLoadingMeta;

  return (
    <>
      <div
        className="employee-form-panel-overlay"
        onClick={onClose}
        aria-hidden="true"
      />

      <aside
        className="employee-form-panel"
        role="dialog"
        aria-modal="true"
        aria-labelledby="employee-form-panel-title"
      >
        <header className="employee-form-panel__header">
          <div>
            <h2 id="employee-form-panel-title" className="employee-form-panel__title">
              {isEditMode ? 'Edit Employee' : 'Add New Employee'}
            </h2>
            <p className="employee-form-panel__subtitle">
              {isEditMode
                ? 'Update employee profile, role and branch details.'
                : 'Create a new staff account and assign permissions.'}
            </p>
          </div>
          <button
            type="button"
            className="employee-form-panel__close"
            onClick={onClose}
            aria-label="Close panel"
          >
            <Cross2Icon />
          </button>
        </header>

        {(error || loadError) && (
          <div className="employee-form-panel__error">
            {loadError || error?.message}
          </div>
        )}

        {isPanelLoading ? (
          <div className="employee-form-panel__loading">
            <Spinner size="medium" />
          </div>
        ) : (
          <>
            <div className="employee-form-panel__body">
              <input
                ref={photoInputRef}
                type="file"
                accept="image/jpeg,image/jpg,image/png,image/webp"
                onChange={handlePhotoSelect}
                style={{ display: 'none' }}
              />

              <div className="employee-form-panel__photo-block">
                {photoError && <div className="photo-error">{photoError}</div>}

                <div
                  className={`photo-slot photo-slot--primary ${photoPreview ? 'photo-slot--filled' : ''}`}
                  onClick={triggerPhotoInput}
                  role="button"
                  tabIndex={0}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      e.preventDefault();
                      triggerPhotoInput();
                    }
                  }}
                >
                  {isUploadingPhoto ? (
                    <Spinner size="small" />
                  ) : photoPreview ? (
                    <>
                      <img src={photoPreview} alt="" className="photo-slot__image" />
                      <button
                        type="button"
                        className="photo-slot__remove"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleRemovePhoto();
                        }}
                        aria-label="Remove photo"
                      >
                        <TrashIcon />
                      </button>
                    </>
                  ) : (
                    <img
                      src={FORM_ICONS.camera}
                      alt=""
                      className="photo-slot__asset-icon"
                    />
                  )}
                </div>

                <button
                  type="button"
                  className="employee-form-panel__upload-link"
                  onClick={triggerPhotoInput}
                  disabled={isUploadingPhoto}
                >
                  {isUploadingPhoto ? 'Uploading...' : 'Upload Profile Picture'}
                </button>
              </div>

              <div className="employee-account-form">
                <Input
                  label="Full Name"
                  value={formData.fullName}
                  onChange={(e) => handleFullNameChange(e.target.value)}
                  placeholder="e.g., Johnathan Doe"
                  required
                />

                <Input
                  label="Email Address"
                  type="email"
                  value={formData.email}
                  onChange={(e) => {
                    handleInputChange('email', e.target.value);
                    onEmailChange(e.target.value);
                  }}
                  onBlur={() => onEmailBlur(formData.email)}
                  error={emailError}
                  placeholder="john.doe@pithad.com"
                  required
                />

                <div className="form-row form-row--2">
                  <Select
                    label="Role"
                    placeholder="Select Role"
                    value={formData.role}
                    onChange={(value) =>
                      handleInputChange('role', value as EmployeeApiRole)
                    }
                    options={roleOptions}
                    searchable={false}
                    required
                  />
                  <Select
                    label="Branch"
                    placeholder="Select Branch"
                    value={formData.branchId}
                    onChange={(value) => handleInputChange('branchId', value)}
                    options={branchOptions}
                    searchable={false}
                    required
                  />
                </div>

                {!isEditMode && (
                  <label className="employee-form-panel__checkbox">
                    <input
                      type="checkbox"
                      checked={formData.sendActivationEmail}
                      onChange={(e) =>
                        handleInputChange('sendActivationEmail', e.target.checked)
                      }
                    />
                    <span>Send account activation email to employee</span>
                  </label>
                )}
              </div>
            </div>

            <footer className="employee-form-panel__footer">
              <Button
                variant="secondary"
                onClick={onClose}
                disabled={isSaving || isLoading}
                className="employee-form-panel__cancel"
              >
                Cancel
              </Button>
              <Button
                onClick={handleSubmit}
                disabled={
                  isSaving || isLoading || !isFormValid || isUploadingPhoto
                }
                className="employee-form-panel__submit"
              >
                {isSaving ? <Spinner size="small" /> : null}
                {isSaving
                  ? 'Saving...'
                  : isEditMode
                    ? 'Save Changes'
                    : 'Create Employee'}
              </Button>
            </footer>
          </>
        )}
      </aside>
    </>
  );
};
